import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Typography } from '@mui/material'
import { DropDown, MButton, TextInput, TextInputMask } from 'components'
import FormDatePicker from 'components/ui/DateTimePicker/DateField'
import HorizontalRadioGroup from 'components/ui/RadioGroup/HorizontalRadioGroup'
import { color } from 'config/color'
import type React from 'react'
import { useForm } from 'react-hook-form'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import * as yup from 'yup'
import { FormType, type InformationFormType } from './ServiceForm'
import dayjs from 'dayjs'

interface InformationFormProps {
    handleNext: () => void
    handleBack: () => void
    formData: InformationFormType
    formType: string
    setFormData: (data: InformationFormType) => void
}

enum MailingPref {
    MailingAddress = 'MAILING_ADDRESS',
    ServiceAddress = 'SERVICE_ADDRESS'

}

export const enum AccountType {
    Residential = 'Residential',
    Commercial = 'Commercial'
}

const today = dayjs().format('YYYY-MM-DD')


const createSchema = (formType: string) => {
    const schemaShape: Record<string, any> = {
        name: yup.string().required('Name is a required field'),
        phone: yup.string().required('Phone number is a required field'),
        email: yup.string().email('Invalid email format').required('Email is a required field')

    }


    if (formType === FormType.Start || formType === FormType.Transfer) {
        schemaShape.ownershipType = yup.string().required('Ownership type is a required field')
        schemaShape.billingPref = yup.string().required('Billing preference is a required field')
        schemaShape.mailingPref = yup.string().required('Mailing preference is a required field')

        schemaShape.mailingAddressLine1 = yup.string().when('mailingPref', {
            is: MailingPref.MailingAddress,
            then: yup.string().required('Mailing address line 1 is a required field')
        })
        schemaShape.mailingCity = yup.string().when('mailingPref', {
            is: MailingPref.MailingAddress,
            then: yup.string().required('Mailing city is a required field')
        })
        schemaShape.mailingState = yup.string().when('mailingPref', {
            is: MailingPref.MailingAddress,
            then: yup.string().required('Mailing state is a required field')
        })
        schemaShape.mailingZipCode = yup.string().when('mailingPref', {
            is: MailingPref.MailingAddress,
            then: yup.string().required('Mailing zip code is a required field')
        })

        schemaShape.ein = yup.string().when('accountType', {
            is: AccountType.Commercial,
            then: yup.string().required('EIN is a required field')
        })

        schemaShape.driversLicense = yup.string().when('accountType', {
            is: AccountType.Residential,
            then: yup.string().required('Driver\'s license is a required field')
        })
        schemaShape.state = yup.string().when('accountType', {
            is: AccountType.Residential,
            then: yup.string().required('State is a required field')
        })
    }

    if (formType === FormType.Start || formType === FormType.Transfer) {
        schemaShape.birthDate = yup.string().when('accountType', {
            is: AccountType.Residential,
            then: yup.string().required('Birth date is required')
        })

        schemaShape.startDate = yup.date().min(today, 'Start date cannot be before today').required('Start date is required')
    }


    if (formType !== FormType.NewTap) {
        schemaShape.accountType = yup.string().required('Account type is a required field')
    }


    if (formType === FormType.Stop) {
        schemaShape.stopDate = yup.date().min(today, 'Stop date cannot be before today').required('Stop date is required')
    }

    if (formType === FormType.Transfer) {
        schemaShape.startDate = yup.date().min(today, 'Start date cannot be before today').required('Start date is required')
        schemaShape.stopDate = yup.date().min(today, 'Stop date cannot be before today').required('Stop date is required')
    }

    if (formType === FormType.Stop || formType === FormType.NewTap) {
        schemaShape.mailingAddressLine1 = yup.string().required('Mailing address line 1 is a required field')
        schemaShape.mailingCity = yup.string().required('Mailing city is a required field')
        schemaShape.mailingState = yup.string().required('Mailing state is a required field')
        schemaShape.mailingZipCode = yup.string().required('Mailing zip code is a required field')
    }


    return yup.object(schemaShape).required()
}


const InformationForm: React.FC<InformationFormProps> = (props: InformationFormProps) => {
    const isMobile = useMobileBreakpoints()
    const { handleNext, handleBack, formData, setFormData, formType } = props
    const schema = createSchema(formType)
    const {
        handleSubmit,
        control,
        watch,
        formState: { errors }
    } = useForm<InformationFormType>({
        mode: 'all',
        defaultValues: {
            name: formData?.name ?? '',
            phone: formData?.phone ?? '',
            email: formData?.email ?? '',
            ein: formData?.ein ?? '',
            driversLicense: formData?.driversLicense ?? '',
            state: formData?.state ?? '',
            comments: '',


            ...((formType === FormType.Stop) && {
                stopDate: formData?.stopDate
            }),
            ...((formType === FormType.Start) && {
                startDate: formData?.startDate,
                birthDate: formData?.birthDate
            }),

            ...((formType === FormType.Transfer) && {
                startDate: formData?.startDate,
                stopDate: formData?.stopDate,
                birthDate: formData?.birthDate
            }),

            ...((formType === FormType.Start || formType === FormType.Transfer) && {
                billingPref: formData?.billingPref ?? '',
                mailingPref: formData?.mailingPref ?? '',
                startDate: formData?.startDate,
                accountType: formData?.accountType ?? '',
                ownershipType: formData?.ownershipType ?? ''
            }),

            ...(formType === FormType.NewTap && {
                svcAddressLine1: formData?.svcAddressLine1 ?? '',
                svcAddressLine2: formData?.svcAddressLine2 ?? '',
                svcAddressCity: formData?.svcAddressCity ?? '',
                svcAddressState: formData?.svcAddressState ?? '',
                svcAddressZipCode: formData?.svcAddressZipCode ?? ''
            }),

            ...((((formType === FormType.Transfer || formType === FormType.NewTap || formType === FormType.Stop) || (formType === FormType.Start && formData?.mailingPref === MailingPref.MailingAddress))) && {
                mailingAddressLine1: formData?.mailingAddressLine1 ?? '',
                mailingAddressLine2: formData?.mailingAddressLine2 ?? '',
                mailingCity: formData?.mailingCity ?? '',
                mailingState: formData?.mailingState ?? '',
                mailingZipCode: formData?.mailingZipCode ?? ''
            })


        },
        resolver: yupResolver(schema)
    })

    const mailingPref = watch('mailingPref')
    console.log(errors)

    const onSubmit = async (data: InformationFormType): Promise<void> => {
        try {
            handleNext()

                ; (formData != null) && setFormData({
                    ...formData,
                    ...data
                })
        } catch (error) {
            console.log(error)
        }
    }

    const ownershipTypeOptions = [
        {
            label: 'Owner',
            value: 'OWNER'
        },
        {
            label: 'Tenant',
            value: 'TENANT'
        }
    ]

    const billingPrefOptions = [
        {
            label: 'Email',
            value: 'EMAIL'
        },
        {
            label: 'Paper',
            value: 'PAPER'
        }
    ]

    const mailingAddressPrefOptions = [
        {
            label: 'Use service address',
            value: MailingPref.ServiceAddress
        },
        {
            label: 'Use a different address',
            value: MailingPref.MailingAddress
        }
    ]

    const accountTypePrefOptions = [
        {
            label: 'Residential',
            value: AccountType.Residential
        },
        {
            label: 'Commercial',
            value: AccountType.Commercial
        }
    ]

    const accountType = watch('accountType')

    const stateOptions = [
        { label: 'Alabama (AL)', value: 'AL' },
        { label: 'Alaska (AK)', value: 'AK' },
        { label: 'Arizona (AZ)', value: 'AZ' },
        { label: 'Arkansas (AR)', value: 'AR' },
        { label: 'California (CA)', value: 'CA' },
        { label: 'Colorado (CO)', value: 'CO' },
        { label: 'Connecticut (CT)', value: 'CT' },
        { label: 'Delaware (DE)', value: 'DE' },
        { label: 'District of Columbia (DC)', value: 'DC' },
        { label: 'Florida (FL)', value: 'FL' },
        { label: 'Georgia (GA)', value: 'GA' },
        { label: 'Hawaii (HI)', value: 'HI' },
        { label: 'Idaho (ID)', value: 'ID' },
        { label: 'Illinois (IL)', value: 'IL' },
        { label: 'Indiana (IN)', value: 'IN' },
        { label: 'Iowa (IA)', value: 'IA' },
        { label: 'Kansas (KS)', value: 'KS' },
        { label: 'Kentucky (KY)', value: 'KY' },
        { label: 'Louisiana (LA)', value: 'LA' },
        { label: 'Maine (ME)', value: 'ME' },
        { label: 'Maryland (MD)', value: 'MD' },
        { label: 'Massachusetts (MA)', value: 'MA' },
        { label: 'Michigan (MI)', value: 'MI' },
        { label: 'Minnesota (MN)', value: 'MN' },
        { label: 'Mississippi (MS)', value: 'MS' },
        { label: 'Missouri (MO)', value: 'MO' },
        { label: 'Montana (MT)', value: 'MT' },
        { label: 'Nebraska (NE)', value: 'NE' },
        { label: 'Nevada (NV)', value: 'NV' },
        { label: 'New Hampshire (NH)', value: 'NH' },
        { label: 'New Jersey (NJ)', value: 'NJ' },
        { label: 'New Mexico (NM)', value: 'NM' },
        { label: 'New York (NY)', value: 'NY' },
        { label: 'North Carolina (NC)', value: 'NC' },
        { label: 'North Dakota (ND)', value: 'ND' },
        { label: 'Ohio (OH)', value: 'OH' },
        { label: 'Oklahoma (OK)', value: 'OK' },
        { label: 'Oregon (OR)', value: 'OR' },
        { label: 'Pennsylvania (PA)', value: 'PA' },
        { label: 'Rhode Island (RI)', value: 'RI' },
        { label: 'South Carolina (SC)', value: 'SC' },
        { label: 'South Dakota (SD)', value: 'SD' },
        { label: 'Tennessee (TN)', value: 'TN' },
        { label: 'Texas (TX)', value: 'TX' },
        { label: 'Utah (UT)', value: 'UT' },
        { label: 'Vermont (VT)', value: 'VT' },
        { label: 'Virginia (VA)', value: 'VA' },
        { label: 'Washington (WA)', value: 'WA' },
        { label: 'West Virginia (WV)', value: 'WV' },
        { label: 'Wisconsin (WI)', value: 'WI' },
        { label: 'Wyoming (WY)', value: 'WY' }
    ]


    return (
        <>
            {(formType !== FormType.NewTap) && <Box mb={2}>
                <Typography variant="h3" fontSize={18} fontWeight={600} mb={2}>
                    Service Information
                </Typography>
                <TextInput
                    fieldLabel="Requestor Name *"
                    fieldName="name"
                    fieldType="text"
                    placeholder="Enter your name or company name"
                    control={control}
                    errors={errors}
                />
                {(formType === FormType.Stop || formType === FormType.Transfer) && <FormDatePicker
                    name="stopDate"
                    control={control}
                    errors={errors}
                    label="Stop Date *"
                    disablePast
                    disableWeekends
                />}
                {(formType === FormType.Start || formType === FormType.Transfer) && <FormDatePicker
                    name="startDate"
                    control={control}
                    errors={errors}
                    label="Start Date *"
                    disablePast
                    disableWeekends
                />}
                <HorizontalRadioGroup
                    name="accountType"
                    control={control}
                    errors={errors}
                    label="Account Type"
                    options={accountTypePrefOptions}
                />
                <HorizontalRadioGroup
                    name="ownershipType"
                    control={control}
                    errors={errors}
                    label="Ownership Type"
                    options={ownershipTypeOptions}
                />

            </Box>}
            <Box mb={2}>
                <Typography variant="h3" fontSize={18} fontWeight={600} mb={2}>
                    Personal Information
                </Typography>
                {(formType === FormType.NewTap) && <TextInput
                    fieldLabel="Customer Name *"
                    fieldName="name"
                    fieldType="text"
                    placeholder="Enter the customer name"
                    control={control}
                    errors={errors}
                />}
                {((formType === FormType.Start || formType === FormType.Transfer) && accountType === AccountType.Residential) && <FormDatePicker
                    name="birthDate"
                    control={control}
                    errors={errors}
                    label="Date of Birth *"
                />}
                <TextInputMask
                    fieldLabel="Phone Number *"
                    fieldName="phone"
                    fieldType="text"
                    placeholder="Enter your phone number"
                    control={control}
                    errors={errors} />
                <TextInput
                    fieldLabel="Email Address *"
                    fieldName="email"
                    fieldType="text"
                    placeholder="Enter your email address"
                    control={control}
                    errors={errors}
                />
                {accountType === AccountType.Commercial && (
                    <TextInput
                        fieldLabel="EIN *"
                        fieldName="ein"
                        fieldType="text"
                        placeholder="Enter your EIN"
                        control={control}
                        errors={errors}
                    />
                )}
                {accountType === AccountType.Residential && (
                    <>
                        <TextInput
                            fieldLabel="Driver's License Number *"
                            fieldName="driversLicense"
                            fieldType="text"
                            placeholder="Enter your driver's license number"
                            control={control}
                            errors={errors}
                        />
                        <DropDown
                            fieldLabel="State *"
                            fieldName="state"
                            placeholder="Enter your state"
                            control={control}
                            errors={errors}
                            options={stateOptions}
                            fieldType="text"
                        />
                    </>
                )}

            </Box>
            {(formType === FormType.Start || formType === FormType.Transfer) && <Box mb={2}>
                <Typography variant="h3" fontSize={18} fontWeight={600} mb={2}>
                    Preferences
                </Typography>
                <HorizontalRadioGroup
                    name="billingPref"
                    control={control}
                    errors={errors}
                    label="Billing Preference"
                    options={billingPrefOptions}
                />
                <HorizontalRadioGroup
                    name="mailingPref"
                    control={control}
                    errors={errors}
                    label="Mailing Address"
                    options={mailingAddressPrefOptions}
                />
                {(mailingPref === MailingPref.MailingAddress) && (
                    <>
                        <Typography
                            my={2}
                            fontSize={14}
                            fontWeight={700}
                            color={color.grey[700]}
                        >
                            Please provide a mailing address for billing
                        </Typography>
                        <TextInput
                            fieldLabel="Address Line 1 *"
                            fieldName="mailingAddressLine1"
                            fieldType="text"
                            placeholder="Enter your address line 1"
                            control={control}
                            errors={errors}
                        />
                        <TextInput
                            fieldLabel="Address Line 2"
                            fieldName="mailingAddressLine2"
                            fieldType="text"
                            placeholder="Enter your address line 2"
                            control={control}
                            errors={errors}
                        />
                        <Box display="flex" justifyContent="space-between" gap={3}>
                            <TextInput
                                fullWidth
                                fieldLabel="City *"
                                fieldName="mailingCity"
                                fieldType="text"
                                placeholder="Enter your city"
                                control={control}
                                errors={errors}
                            />
                            <TextInput
                                fullWidth
                                fieldLabel="State *"
                                fieldName="mailingState"
                                fieldType="text"
                                placeholder="Enter your state"
                                control={control}
                                errors={errors}
                            />
                            <TextInput
                                fullWidth
                                fieldLabel="Zip Code *"
                                fieldName="mailingZipCode"
                                fieldType="text"
                                placeholder="Enter your zip code"
                                control={control}
                                errors={errors}
                            />
                        </Box>
                    </>
                )}
            </Box>}
            {(formType === FormType.NewTap) && <Box mb={2}>
                <Typography variant="h3" fontSize={18} fontWeight={600} mb={2}>
                    Service Address
                </Typography>
                <>
                    <TextInput
                        fieldLabel="Address Line 1 *"
                        fieldName="svcAddressLine1"
                        fieldType="text"
                        placeholder="Enter your address line 1"
                        control={control}
                        errors={errors}
                    />
                    <TextInput
                        fieldLabel="Address Line 2"
                        fieldName="svcAddressLine2"
                        fieldType="text"
                        placeholder="Enter your address line 2"
                        control={control}
                        errors={errors}
                    />
                    <Box display="flex" justifyContent="space-between" gap={3}>
                        <TextInput
                            fullWidth
                            fieldLabel="City *"
                            fieldName="svcAddressCity"
                            fieldType="text"
                            placeholder="Enter your city"
                            control={control}
                            errors={errors}
                        />
                        <TextInput
                            fullWidth
                            fieldLabel="State *"
                            fieldName="svcAddressState"
                            fieldType="text"
                            placeholder="Enter your state"
                            control={control}
                            errors={errors}
                        />
                        <TextInput
                            fullWidth
                            fieldLabel="Zip Code *"
                            fieldName="svcAddressZipCode"
                            fieldType="text"
                            placeholder="Enter your zip code"
                            control={control}
                            errors={errors}
                        />
                    </Box>
                </>
            </Box>}
            {(formType === FormType.Stop || formType === FormType.NewTap) && <Box mb={2}>
                <Typography variant="h3" fontSize={18} fontWeight={600} mb={2}>
                    Mailing Address
                </Typography>
                <>
                    <TextInput
                        fieldLabel="Address Line 1 *"
                        fieldName="mailingAddressLine1"
                        fieldType="text"
                        placeholder="Enter your address line 1"
                        control={control}
                        errors={errors}
                    />
                    <TextInput
                        fieldLabel="Address Line 2"
                        fieldName="mailingAddressLine2"
                        fieldType="text"
                        placeholder="Enter your address line 2"
                        control={control}
                        errors={errors}
                    />
                    <Box display="flex" justifyContent="space-between" gap={3}>
                        <TextInput
                            fullWidth
                            fieldLabel="City *"
                            fieldName="mailingCity"
                            fieldType="text"
                            placeholder="Enter your city"
                            control={control}
                            errors={errors}
                        />
                        <TextInput
                            fullWidth
                            fieldLabel="State *"
                            fieldName="mailingState"
                            fieldType="text"
                            placeholder="Enter your state"
                            control={control}
                            errors={errors}
                        />
                        <TextInput
                            fullWidth
                            fieldLabel="Zip Code *"
                            fieldName="mailingZipCode"
                            fieldType="text"
                            placeholder="Enter your zip code"
                            control={control}
                            errors={errors}
                        />
                    </Box>
                </>
            </Box>}
            <TextInput
                fieldLabel="Comments"
                fieldName="comments"
                fieldType="text"
                placeholder="Enter your comments"
                multiline
                control={control}
                errors={errors}
            />

            <Box
                display="flex"
                my={3}
                width={isMobile ? '100%' : '50%'}
                flexDirection={isMobile ? 'column' : 'row'}
                gap={2}
            >
                <MButton
                    fullWidth
                    variant="outlined"
                    texttransform="none"
                    rounded="true"
                    onClick={() => {
                        handleBack()
                    }}
                >
                    Back
                </MButton>
                <MButton
                    fullWidth
                    variant="contained"
                    type="submit"
                    texttransform="none"
                    rounded="true"
                    onClick={handleSubmit(onSubmit)}
                >
                    Review Information
                </MButton>
            </Box>
        </>
    )
}

export default InformationForm
